import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Bot, Copy, Check, ChevronDown, ChevronRight, Loader2, Mail, Send, Sparkles, Link2, Eye, Users,
} from "lucide-react";
import { InviteEmailRichEditor } from "@/components/InviteEmailRichEditor";
import {
  DEFAULT_INVITE_CTA_LABEL,
  DEFAULT_INVITE_EXTRAS_HTML,
} from "@/lib/inviteEmailSnippets";

const AI_MODES = [
  { id: "draft", label: "Teljes szöveg" },
  { id: "shorter", label: "Rövidebb" },
  { id: "formal", label: "Formálisabb" },
  { id: "urgency", label: "Sürgősség" },
  { id: "cta", label: "Erősebb CTA" },
];

const DEFAULT_BODY =
  "<p>Kedves {name}!</p><p>Az <b>AutoPark</b> egy magyar használtautó marketplace — 360° bemutató, AI háttér, kredites hirdetés. Pilot partnereinknek <b>{credits} kredit</b> induló ajándékot adunk.</p><p>Ha érdekel, regisztrálj az alábbi gombbal — pár perc alatt fent lehet az első hirdetésed.</p>";

const RECIPIENT_STATUS_LABEL = {
  sent: { label: "Elküldve", className: "text-emerald-400" },
  skipped_registered: { label: "Kihagyva (már regisztrált)", className: "text-amber-400" },
  failed: { label: "Hiba", className: "text-red-400" },
  pending: { label: "Folyamatban", className: "text-slate-400" },
};

function recipientStatus(status) {
  return RECIPIENT_STATUS_LABEL[status] || { label: status || "—", className: "text-slate-400" };
}

export function AdminInviteEmails() {
  const { user } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [promoSlug, setPromoSlug] = useState("");
  const [subject, setSubject] = useState("Meghívó az AutoPark pilot programba");
  const [preheader, setPreheader] = useState("");
  const [heading, setHeading] = useState("Meghívó az AutoPark pilot programba");
  const [bodyHtml, setBodyHtml] = useState(DEFAULT_BODY);
  const [extrasHtml, setExtrasHtml] = useState(DEFAULT_INVITE_EXTRAS_HTML);
  const [ctaLabel, setCtaLabel] = useState(DEFAULT_INVITE_CTA_LABEL);
  const [recipientsText, setRecipientsText] = useState("");
  const [parsed, setParsed] = useState([]);
  const [skipRegistered, setSkipRegistered] = useState(true);
  const [includeExtras, setIncludeExtras] = useState(true);
  const [aiInstruction, setAiInstruction] = useState(
    "Írj barátságos B2B meghívót autókereskedőnek, emeld ki a 500 kreditet és a 360° funkciót.",
  );
  const [audience, setAudience] = useState("független autókereskedő");
  const [loadingCtx, setLoadingCtx] = useState(true);
  const [aiLoading, setAiLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const [batches, setBatches] = useState([]);
  const [expandedBatchId, setExpandedBatchId] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(null);
  const [emailHint, setEmailHint] = useState("");
  const [inboxAvatarHint, setInboxAvatarHint] = useState("");
  const [brandLogoUrl, setBrandLogoUrl] = useState("");

  const loadCtx = useCallback(async () => {
    setLoadingCtx(true);
    try {
      const q = promoSlug ? `?promo_slug=${encodeURIComponent(promoSlug)}` : "";
      const r = await api.get(`/admin/invite-emails/context${q}`);
      setCtx(r.data);
      if (!promoSlug && r.data.promo?.slug) {
        setPromoSlug(r.data.promo.slug);
      }
      setPreheader((prev) =>
        prev || `${r.data.credits} kredit + 360° — regisztráció pár perc alatt`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || "Betöltés sikertelen");
    } finally {
      setLoadingCtx(false);
    }
  }, [promoSlug]);

  const loadBatches = useCallback(async () => {
    try {
      const r = await api.get("/admin/invite-emails/batches");
      setBatches(r.data.batches || []);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadBatches();
    api.get("/admin/invite-emails/delivery-status")
      .then((r) => {
        setEmailConfigured(r.data.configured);
        setEmailHint(r.data.hint || "");
        setInboxAvatarHint(r.data.inbox_avatar_hint || "");
        setBrandLogoUrl(r.data.brand_logo_url || "");
      })
      .catch(() => setEmailConfigured(null));
  }, [loadBatches]);

  useEffect(() => {
    loadCtx();
  }, [loadCtx]);

  const inviteLink = ctx?.link || "";

  const copyLink = async () => {
    if (!inviteLink) return;
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopied(true);
      toast.success("Link másolva");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Másolás sikertelen");
    }
  };

  const parseRecipients = async () => {
    try {
      const r = await api.post("/admin/invite-emails/parse-recipients", {
        recipients_text: recipientsText,
      });
      setParsed(r.data.recipients || []);
      toast.success(`${r.data.count} címzett`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Feldolgozás sikertelen");
    }
  };

  const runAi = async (mode) => {
    setAiLoading(true);
    try {
      const r = await api.post("/admin/invite-emails/ai-compose", {
        instruction: aiInstruction,
        mode,
        promo_slug: promoSlug || undefined,
        audience,
        subject,
        preheader,
        body_html: bodyHtml,
      });
      setSubject(r.data.subject || subject);
      setPreheader(r.data.preheader || preheader);
      setBodyHtml(r.data.body_html || bodyHtml);
      toast.success("AI szöveg beillesztve");
    } catch (e) {
      toast.error(e.response?.data?.detail || "AI hiba");
    } finally {
      setAiLoading(false);
    }
  };

  const loadPreview = async () => {
    try {
      const r = await api.post("/admin/invite-emails/preview", {
        promo_slug: promoSlug || undefined,
        subject,
        preheader,
        heading,
        body_html: bodyHtml,
        include_extras: includeExtras,
        extras_html: extrasHtml,
        cta_label: ctaLabel,
      });
      setPreviewHtml(r.data.html || "");
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Előnézet sikertelen");
    }
  };

  const send = async (testOnly) => {
    setSending(true);
    try {
      const r = await api.post("/admin/invite-emails/send", {
        promo_slug: promoSlug || undefined,
        subject,
        preheader,
        heading,
        body_html: bodyHtml,
        recipients_text: recipientsText,
        skip_registered: skipRegistered,
        test_only: testOnly,
        test_email: testOnly ? user?.email : undefined,
        include_extras: includeExtras,
        extras_html: extrasHtml,
        cta_label: ctaLabel,
      });
      if (testOnly) {
        if (r.data.sent) toast.success("Teszt email elküldve — nézd a spam mappát is");
        else toast.error(r.data.error || emailHint || "Küldés sikertelen — EMERGENT_EMAIL_KEY?");
      } else {
        const c = r.data.counts || {};
        toast.success(`Kész: ${c.sent} elküldve, ${c.skipped} kihagyva, ${c.failed} hiba`);
        loadBatches();
        setExpandedBatchId(r.data.batch_id || null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Küldés sikertelen");
    } finally {
      setSending(false);
    }
  };

  if (loadingCtx && !ctx) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="w-8 h-8 text-electric animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-invite-emails" className="space-y-6">
      {emailConfigured === false && (
        <div
          data-testid="invite-email-not-configured"
          className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-100"
        >
          <b>Email küldés nincs beállítva a szerveren.</b> {emailHint}
        </div>
      )}
      <div className="glass rounded-2xl p-5 border border-white/5">
        <h3 className="font-heading text-lg font-medium flex items-center gap-2 mb-1">
          <Link2 className="w-5 h-5 text-electric" /> Regisztrációs meghívó link
        </h3>
        <p className="text-sm text-slate-400 mb-4">
          A levélben automatikusan szerepel a promó link UTM paraméterekkel (batch nyomon követés).
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-xs text-slate-500">
            Promó link
            <select
              data-testid="invite-promo-select"
              value={promoSlug}
              onChange={(e) => setPromoSlug(e.target.value)}
              className="mt-1 w-full bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm"
            >
              {(ctx?.promos || []).map((p) => (
                <option key={p.id || p.slug} value={p.slug}>
                  {p.label || p.slug} — {p.credits_target} kr / {p.duration_days} nap
                </option>
              ))}
            </select>
          </label>
          <div className="text-xs text-slate-500 flex flex-col justify-end">
            <span>
              Becslés: <b className="text-emerald-400">{ctx?.days_estimate ?? "—"} nap</b> hirdetés az ajándék
              kreditből (napi {ctx?.listing_daily ?? 10} kr)
            </span>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <code className="text-xs text-slate-300 bg-black/30 px-2 py-1 rounded break-all flex-1 min-w-0">
            {inviteLink}
          </code>
          <button
            type="button"
            data-testid="invite-copy-link"
            onClick={copyLink}
            className="inline-flex items-center gap-1 text-sm px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
            Másolás
          </button>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="glass rounded-2xl p-5 border border-white/5 space-y-4">
          <h3 className="font-heading text-lg flex items-center gap-2">
            <Mail className="w-5 h-5 text-electric" /> Levél
          </h3>
          <input
            data-testid="invite-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Tárgy"
            className="w-full bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm"
          />
          <input
            data-testid="invite-preheader"
            value={preheader}
            onChange={(e) => setPreheader(e.target.value)}
            placeholder="Előnézet sor (preheader)"
            className="w-full bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm"
          />
          <input
            value={heading}
            onChange={(e) => setHeading(e.target.value)}
            placeholder="Fejléc a levélben"
            className="w-full bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm"
          />
          <p className="text-xs text-slate-500 font-medium uppercase tracking-wide">Levél törzs</p>
          <InviteEmailRichEditor
            data-testid="invite-body"
            value={bodyHtml}
            onChange={setBodyHtml}
          />
          <label className="flex items-center gap-2 text-sm text-slate-400">
            <input
              type="checkbox"
              checked={includeExtras}
              onChange={(e) => setIncludeExtras(e.target.checked)}
            />
            Promó blokk megjelenítése (kredit kártya + funkciók)
          </label>
          {includeExtras ? (
            <div className="space-y-3 rounded-xl border border-electric/20 bg-electric/5 p-4">
              <div>
                <p className="text-sm font-medium text-slate-200">Promó blokk</p>
                <p className="text-xs text-slate-400 mt-0.5">
                  Ez jelenik meg a törzs alatt az előnézetben. Használd a{" "}
                  <kbd className="px-1 rounded bg-white/10 font-mono text-[10px]">/</kbd> menüt: kredit kártya, pilot
                  funkciók. Placeholderek: {"{credits}"}, {"{days}"}, {"{days_estimate}"}.
                </p>
              </div>
              <InviteEmailRichEditor
                data-testid="invite-extras"
                value={extrasHtml}
                onChange={setExtrasHtml}
              />
              <label className="block text-xs text-slate-500">
                Regisztrációs gomb felirata
                <input
                  data-testid="invite-cta-label"
                  value={ctaLabel}
                  onChange={(e) => setCtaLabel(e.target.value)}
                  placeholder={DEFAULT_INVITE_CTA_LABEL}
                  className="mt-1 w-full bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm"
                />
              </label>
              <p className="text-[11px] text-slate-500">
                A link, QR kód és a gomb stílusa automatikus — csak a felirat és a promó HTML szerkeszthető.
              </p>
            </div>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              data-testid="invite-preview"
              onClick={loadPreview}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-sm hover:bg-white/10"
            >
              <Eye className="w-4 h-4" /> Előnézet
            </button>
            <button
              type="button"
              data-testid="invite-test-send"
              disabled={sending}
              onClick={() => send(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-600/80 text-sm hover:bg-amber-600 disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Teszt ({user?.email})
            </button>
          </div>
        </div>

        <div className="space-y-4">
          <div className="glass rounded-2xl p-5 border border-white/5 space-y-3">
            <h3 className="font-heading text-lg flex items-center gap-2">
              <Bot className="w-5 h-5 text-electric" /> AI szövegsegéd
            </h3>
            <input
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              placeholder="Célközönség"
              className="w-full bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm"
            />
            <textarea
              data-testid="invite-ai-instruction"
              value={aiInstruction}
              onChange={(e) => setAiInstruction(e.target.value)}
              rows={3}
              className="w-full bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm"
            />
            <div className="flex flex-wrap gap-2">
              {AI_MODES.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`invite-ai-${m.id}`}
                  disabled={aiLoading}
                  onClick={() => runAi(m.id)}
                  className="text-xs px-2.5 py-1.5 rounded-full bg-electric/20 text-electric hover:bg-electric/30 disabled:opacity-50"
                >
                  {aiLoading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : <Sparkles className="w-3 h-3 inline mr-1" />}
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="glass rounded-2xl p-5 border border-white/5 space-y-3">
            <h3 className="font-heading text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-electric" /> Címzettek
            </h3>
            <textarea
              data-testid="invite-recipients"
              value={recipientsText}
              onChange={(e) => setRecipientsText(e.target.value)}
              rows={8}
              placeholder={"email@ceg.hu\nNév <masik@ceg.hu>\nemail;Név"}
              className="w-full bg-anthracite-900 border border-white/10 rounded-lg p-2 text-sm font-mono"
            />
            <button
              type="button"
              onClick={parseRecipients}
              className="text-sm text-electric hover:underline"
            >
              Lista ellenőrzése
            </button>
            {parsed.length > 0 && (
              <ul className="text-xs text-slate-400 max-h-32 overflow-y-auto space-y-1">
                {parsed.map((p) => (
                  <li key={p.email}>
                    {p.email}
                    {p.registered && (
                      <span className="text-amber-400 ml-2">(már regisztrált)</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
            <label className="flex items-center gap-2 text-sm text-slate-400">
              <input
                type="checkbox"
                checked={skipRegistered}
                onChange={(e) => setSkipRegistered(e.target.checked)}
              />
              Regisztrált címek kihagyása
            </label>
            <button
              type="button"
              data-testid="invite-send-batch"
              disabled={sending || !recipientsText.trim()}
              onClick={() => send(false)}
              className="w-full inline-flex justify-center items-center gap-2 px-4 py-3 rounded-xl bg-electric text-white font-medium hover:bg-electric/90 disabled:opacity-50"
            >
              {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              Meghívók küldése
            </button>
            <p className="text-[11px] text-slate-500">
              Feladó megjelenő név: AutoPark. A tényleges cím (pl. info@autoparks.hu) az Emergent email
              fiókban van beállítva. Szerver: <code className="text-slate-400">EMERGENT_EMAIL_KEY</code> a backend .env-ben.
            </p>
            {inboxAvatarHint ? (
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {inboxAvatarHint}
                {brandLogoUrl ? (
                  <>
                    {" "}
                    <a href={brandLogoUrl} target="_blank" rel="noreferrer" className="text-electric hover:underline">
                      Villám ikon (favicon)
                    </a>
                  </>
                ) : null}
              </p>
            ) : null}
          </div>
        </div>
      </div>

      {batches.length > 0 && (
        <div className="glass rounded-2xl p-5 border border-white/5" data-testid="invite-batch-history">
          <h3 className="font-heading text-lg mb-1">Korábbi kötegek</h3>
          <p className="text-xs text-slate-500 mb-3">
            Kattints egy sorra a címzettek listájához. A teszt küldések itt nem jelennek meg.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-slate-500 text-xs">
                <tr>
                  <th className="py-2 w-8" aria-label="Részletek" />
                  <th className="py-2">Dátum</th>
                  <th>Promó</th>
                  <th>Tárgy</th>
                  <th>Elküldve</th>
                  <th>Kihagyva</th>
                  <th>Hiba</th>
                </tr>
              </thead>
              <tbody>
                {batches.map((b) => {
                  const open = expandedBatchId === b.id;
                  const recipients = Array.isArray(b.recipients) ? b.recipients : [];
                  return (
                    <React.Fragment key={b.id}>
                      <tr
                        className="border-t border-white/5 cursor-pointer hover:bg-white/[0.03]"
                        onClick={() => setExpandedBatchId(open ? null : b.id)}
                        data-testid={`invite-batch-row-${b.id}`}
                      >
                        <td className="py-2 text-slate-500">
                          {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </td>
                        <td className="py-2 text-slate-400">{b.created_at?.slice(0, 16)?.replace("T", " ")}</td>
                        <td>{b.promo_slug}</td>
                        <td className="max-w-[200px] truncate">{b.subject}</td>
                        <td className="text-emerald-400">{b.counts?.sent ?? 0}</td>
                        <td>{b.counts?.skipped ?? 0}</td>
                        <td className="text-red-400">{b.counts?.failed ?? 0}</td>
                      </tr>
                      {open ? (
                        <tr className="border-t border-white/5 bg-black/20">
                          <td colSpan={7} className="py-3 px-2">
                            {recipients.length === 0 ? (
                              <p className="text-xs text-slate-500">Nincs címzett adat ehhez a köteghez.</p>
                            ) : (
                              <table className="w-full text-xs" data-testid={`invite-batch-recipients-${b.id}`}>
                                <thead className="text-slate-500">
                                  <tr>
                                    <th className="text-left py-1 pr-2 font-medium">Email</th>
                                    <th className="text-left py-1 pr-2 font-medium">Név</th>
                                    <th className="text-left py-1 font-medium">Státusz</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {recipients.map((r) => {
                                    const st = recipientStatus(r.status);
                                    return (
                                      <tr key={`${b.id}-${r.email}`} className="border-t border-white/5">
                                        <td className="py-1.5 pr-2 font-mono text-slate-300">{r.email}</td>
                                        <td className="py-1.5 pr-2 text-slate-400">{r.name || "—"}</td>
                                        <td className={`py-1.5 ${st.className}`}>
                                          {st.label}
                                          {r.error ? (
                                            <span className="block text-[10px] text-red-300/80 mt-0.5">{r.error}</span>
                                          ) : null}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            )}
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {previewOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setPreviewOpen(false)}>
          <div
            className="bg-anthracite-900 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-white/10">
              <span className="font-medium">Email előnézet</span>
              <button type="button" className="text-sm text-slate-400 hover:text-white" onClick={() => setPreviewOpen(false)}>
                Bezárás
              </button>
            </div>
            <iframe title="invite-preview" srcDoc={previewHtml} className="w-full h-[70vh] bg-white" />
          </div>
        </div>
      )}
    </div>
  );
}
